import React, { useMemo } from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import QuoteForm from '../components/QuoteForm';
import { COMPANY } from '../mock';

const Contact = () => {
  // Memoised to avoid new array refs on every render
  const phoneLines = useMemo(() => [
    { href: `tel:${COMPANY.phone.replace(/\s/g, '')}`, text: COMPANY.phone },
  ], []);
  const emailLines = useMemo(() => [
    { href: `mailto:${COMPANY.email}`, text: COMPANY.email },
  ], []);
  const addressLines = useMemo(() => [{ text: COMPANY.address }], []);
  const hoursLines = useMemo(() => [{ text: COMPANY.hours }], []);

  return (
    <>
      <PageHeader
        crumb="Contact"
        title="Contact Move Van"
        subtitle="Call, email or send us the details below — we respond within minutes, 24 hours a day, 7 days a week."
      />

      <section className="section">
        <div className="container-xl grid grid-cols-1 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-5">
            <ContactCard icon={Phone} title="Call us" lines={phoneLines} />
            <ContactCard icon={Mail} title="Email us" lines={emailLines} />
            <ContactCard icon={MapPin} title="Visit us" lines={addressLines} />
            <ContactCard icon={Clock} title="Opening hours" lines={hoursLines} />
            <a href="https://wa.me/447985291097" target="_blank" rel="noreferrer" className="btn-primary w-full justify-center">
              <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
            </a>
          </div>

          <div className="lg:col-span-3">
            <QuoteForm compact />
          </div>
        </div>
      </section>

      <section className="pb-12">
        <div className="container-xl">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 flex flex-wrap items-center justify-between gap-4" data-testid="contact-map-link">
            <div>
              <div className="font-bold text-[color:var(--mv-navy)]">Registered office</div>
              <div className="text-sm text-slate-600">{COMPANY.address}</div>
              <div className="text-xs text-slate-500 mt-1">We come to you — this is our postal address, not a depot for drop-offs.</div>
            </div>
            <a href="https://www.google.com/maps/search/?api=1&query=66%20Paul%20Street%2C%20London%20EC2A%204NA" target="_blank" rel="noreferrer" className="btn-outline-dark" data-testid="contact-open-google-maps">Open in Google Maps</a>
          </div>
        </div>
      </section>
    </>
  );
};

const ContactCard = ({ icon: Icon, title, lines }) => (
  <div className="bg-white rounded-xl p-4 sm:p-6 border border-slate-100 flex gap-3 sm:gap-4">
    <div className="w-11 h-11 shrink-0 rounded-md bg-orange-50 text-[color:var(--mv-orange)] flex items-center justify-center">
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0 break-words">
      <div className="text-sm text-slate-500 font-semibold uppercase tracking-wider">{title}</div>
      <div className="mt-1 space-y-0.5">
        {lines.map((l, i) => (
          l.href ? (
            <a key={l.text} href={l.href} className="block text-[color:var(--mv-navy)] font-bold hover:text-[color:var(--mv-orange)] transition-colors">{l.text}</a>
          ) : (
            <div key={l.text} className="text-[color:var(--mv-navy)] font-bold">{l.text}</div>
          )
        ))}
      </div>
    </div>
  </div>
);

export default Contact;
